import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ChevronLeft, TrendingUp, FileSignature } from "lucide-react";
import { toast } from "sonner";
import ESignatureDialog from "@/components/ESignatureDialog";
import { useAuth } from "@/context/AuthContext";
import logger from "@/lib/logger";

/**
 * RiskTrendPage — full-screen deep-dive on the residual risk sparkline
 * shown on DeviceDetail. QA can toggle 6-month / 12-month windows and
 * inspect a stacked bar breakdown by residual risk level (High / Medium /
 * Low / Unset) per ISO week.
 *
 * Route: /devices/:id/risk-trend
 * Backend: reuses GET /api/risks/summary/trend?device_id=X&days=N — no
 * server change required; the endpoint already returns per-bucket
 * High/Medium/Low/Unset counts.
 */
export default function RiskTrendPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const isAdmin = (user?.roles || []).includes("Admin");
  const [days, setDays] = useState(180);
  const [device, setDevice] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!id) return;
    api.get(`/devices/${id}`)
      .then(({ data }) => setDevice(data))
      .catch((e) => logger.warn("device load:", e?.response?.status));
  }, [id]);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    api.get(`/risks/summary/trend?device_id=${encodeURIComponent(id)}&days=${days}`)
      .then(({ data }) => setData(data))
      .catch((e) => logger.warn("risk trend:", e?.response?.status))
      .finally(() => setLoading(false));
  }, [id, days]);

  const buckets = data?.buckets || [];
  const totals = useMemo(() => buckets.map((b) => b.total), [buckets]);
  const max = Math.max(1, ...totals);

  // Sign & export handler. Admin-only. On successful e-signature the
  // returned signature_id is passed to /risks/trend/export/pdf which prints
  // the Part-11 signature block on the cover — matching the register binder.
  const exportTrend = async (signatureId = null) => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ device_id: id, days: String(days) });
      if (signatureId) params.set("signature_id", signatureId);
      const resp = await api.get(`/risks/trend/export/pdf?${params.toString()}`,
        { responseType: "blob", timeout: 45000 });
      const eventId = resp.headers["x-audit-event-id"] || resp.headers["X-Audit-Event-Id"];
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `risk_trend_${id}${signatureId ? "_signed" : ""}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success(
        `${signatureId ? "Signed t" : "T"}rend PDF exported${eventId ? ` · event ${eventId}` : ""}`,
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto space-y-6" data-testid="risk-trend-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <Link
              to={`/devices/${id}`}
              className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"
              data-testid="risk-trend-back"
            >
              <ChevronLeft className="h-4 w-4" /> Back to device
            </Link>
            <h1 className="font-display text-3xl font-bold text-slate-900 mt-1">
              <TrendingUp className="inline h-7 w-7 mr-2 text-vq-blue" />
              Residual risk trend
            </h1>
            <div className="text-sm text-slate-500 mt-1">
              {device?.device_name || id}
              {data && (
                <span className="ml-2">
                  · {data.total_in_window} risk{data.total_in_window === 1 ? "" : "s"} in window
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {isAdmin && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="risk-trend-export-btn"
                  onClick={() => exportTrend()}
                  disabled={exporting || buckets.length === 0}
                  title="Audit-logged PDF export of the trend as shown. Header carries the event ID."
                >
                  <FileSignature className="h-4 w-4 mr-1.5" />
                  Export PDF
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  data-testid="risk-trend-export-signed-btn"
                  onClick={() => setSignOpen(true)}
                  disabled={exporting || buckets.length === 0}
                  className="bg-vq-blue text-white hover:bg-vq-blue/90"
                  title="Sign this trend and export it as a Part-11 PDF suitable for the DHF."
                >
                  <FileSignature className="h-4 w-4 mr-1.5" />
                  Sign &amp; export
                </Button>
              </>
            )}
            <div className="inline-flex rounded-lg border border-slate-200 p-1 bg-white" data-testid="risk-trend-window-toggle">
            {[
              { d: 180, label: "6 months" },
              { d: 365, label: "12 months" },
            ].map((opt) => (
              <button
                key={opt.d}
                type="button"
                onClick={() => setDays(opt.d)}
                data-testid={`risk-trend-window-${opt.d}`}
                className={`px-3 py-1.5 text-sm rounded-md transition ${
                  days === opt.d
                    ? "bg-vq-blue text-white font-semibold"
                    : "text-slate-600 hover:bg-slate-50"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          </div>
        </div>

        {/* Line chart */}
        <section className="vq-card p-5" data-testid="risk-trend-line-card">
          <div className="vq-overline">Trend</div>
          <div className="font-display font-bold text-slate-900 mt-1">
            New risks per ISO week
          </div>
          {loading ? (
            <div className="py-10 text-center text-sm text-slate-500">Loading…</div>
          ) : buckets.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-500" data-testid="risk-trend-line-empty">
              No risks created in the last {days} days on this device.
            </div>
          ) : (
            <LineChart buckets={buckets} max={max} />
          )}
        </section>

        {/* Stacked bar chart */}
        <section className="vq-card p-5" data-testid="risk-trend-stacked-card">
          <div className="vq-overline">Breakdown</div>
          <div className="font-display font-bold text-slate-900 mt-1">
            Residual risk level per week
          </div>
          {loading ? (
            <div className="py-10 text-center text-sm text-slate-500">Loading…</div>
          ) : buckets.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-500" data-testid="risk-trend-stacked-empty">
              Nothing to break down yet.
            </div>
          ) : (
            <StackedBar buckets={buckets} />
          )}
          <Legend />
        </section>
      </div>
      {isAdmin && (
        <ESignatureDialog
          open={signOpen}
          onOpenChange={setSignOpen}
          entityType="risk_trend"
          entityId={id || "device"}
          action="risk_trend_export"
          defaultMeaning="Approval — Risk Trend Export"
          title="Sign Risk Trend Export"
          description="21 CFR Part 11 · your signature will print on the trend PDF cover along with the audit event ID."
          onSigned={(sig) => {
            setSignOpen(false);
            exportTrend(sig.signature_id);
          }}
        />
      )}
    </DashboardLayout>
  );
}

// ---- Charts (pure SVG so we don't pull in a chart lib) ----

const LEVEL_COLORS = {
  High: "#dc2626",
  Medium: "#f59e0b",
  Low: "#059669",
  Unset: "#94a3b8",
};

function LineChart({ buckets, max }) {
  const W = 720, H = 200, PAD_L = 32, PAD_R = 8, PAD_T = 14, PAD_B = 24;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;
  const xFor = (i) =>
    PAD_L + (buckets.length === 1 ? innerW / 2 : (i * innerW) / (buckets.length - 1));
  const yFor = (v) => PAD_T + innerH - (v / max) * innerH;
  const pts = buckets.map((b, i) => `${xFor(i).toFixed(1)},${yFor(b.total).toFixed(1)}`).join(" ");

  // Y-axis ticks: 0, mid, max
  const ticks = [0, Math.ceil(max / 2), max];

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="w-full h-56"
      data-testid="risk-trend-line-svg"
      role="img"
      aria-label={`Line chart with ${buckets.length} weekly points`}
    >
      {/* Y grid */}
      {ticks.map((t) => (
        <g key={`yg-${t}`}>
          <line
            x1={PAD_L} x2={W - PAD_R}
            y1={yFor(t)} y2={yFor(t)}
            stroke="#e2e8f0" strokeWidth="1"
          />
          <text
            x={PAD_L - 6} y={yFor(t) + 4}
            fontSize="10" fill="#64748b" textAnchor="end"
          >{t}</text>
        </g>
      ))}
      {/* Line */}
      <polyline fill="none" stroke="#0055FF" strokeWidth="2" points={pts} />
      {/* Dots */}
      {buckets.map((b, i) => (
        <circle
          key={`ln-${b.week}`}
          cx={xFor(i)} cy={yFor(b.total)} r={3} fill="#0055FF"
          data-testid={`risk-trend-point-${b.week}`}
        >
          <title>
            {`${b.week} · ${b.total} total (H:${b.High} M:${b.Medium} L:${b.Low}${b.Unset ? ` U:${b.Unset}` : ""})`}
          </title>
        </circle>
      ))}
      {/* X labels — first, middle, last only so we don't overlap */}
      {[0, Math.floor(buckets.length / 2), buckets.length - 1]
        .filter((v, i, arr) => arr.indexOf(v) === i)
        .map((i) => (
          <text
            key={`xl-${i}`}
            x={xFor(i)} y={H - 6}
            fontSize="10" fill="#64748b" textAnchor="middle"
          >{buckets[i]?.week}</text>
        ))}
    </svg>
  );
}

function StackedBar({ buckets }) {
  const W = 720, H = 220, PAD_L = 32, PAD_R = 8, PAD_T = 14, PAD_B = 24;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;
  const max = Math.max(1, ...buckets.map((b) => b.total));
  const bw = Math.max(4, (innerW / buckets.length) - 4);
  const xFor = (i) => PAD_L + i * (innerW / buckets.length) + 2;
  const yFor = (v) => PAD_T + innerH - (v / max) * innerH;
  const hFor = (v) => (v / max) * innerH;

  const ticks = [0, Math.ceil(max / 2), max];

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="w-full h-60"
      data-testid="risk-trend-stacked-svg"
      role="img"
      aria-label={`Stacked bar chart with ${buckets.length} weeks`}
    >
      {ticks.map((t) => (
        <g key={`sg-${t}`}>
          <line
            x1={PAD_L} x2={W - PAD_R}
            y1={yFor(t)} y2={yFor(t)}
            stroke="#e2e8f0" strokeWidth="1"
          />
          <text
            x={PAD_L - 6} y={yFor(t) + 4}
            fontSize="10" fill="#64748b" textAnchor="end"
          >{t}</text>
        </g>
      ))}
      {buckets.map((b, i) => {
        let stackTop = PAD_T + innerH;
        return (
          <g key={`sb-${b.week}`} data-testid={`risk-trend-stack-${b.week}`}>
            {["Low", "Medium", "High", "Unset"].map((lvl) => {
              const v = b[lvl] || 0;
              if (v <= 0) return null;
              const barH = hFor(v);
              stackTop -= barH;
              return (
                <rect
                  key={`sb-${b.week}-${lvl}`}
                  x={xFor(i)} y={stackTop}
                  width={bw} height={barH}
                  fill={LEVEL_COLORS[lvl]}
                  data-testid={`risk-trend-stack-${b.week}-${lvl}`}
                >
                  <title>{`${b.week} · ${lvl}: ${v}`}</title>
                </rect>
              );
            })}
          </g>
        );
      })}
      {[0, Math.floor(buckets.length / 2), buckets.length - 1]
        .filter((v, i, arr) => arr.indexOf(v) === i)
        .map((i) => (
          <text
            key={`sx-${i}`}
            x={xFor(i) + bw / 2} y={H - 6}
            fontSize="10" fill="#64748b" textAnchor="middle"
          >{buckets[i]?.week}</text>
        ))}
    </svg>
  );
}

function Legend() {
  return (
    <div className="flex items-center gap-4 mt-3 text-xs text-slate-600" data-testid="risk-trend-legend">
      {["High", "Medium", "Low", "Unset"].map((lvl) => (
        <span key={lvl} className="inline-flex items-center gap-1.5">
          <span
            className="inline-block h-2.5 w-2.5 rounded-sm"
            style={{ backgroundColor: LEVEL_COLORS[lvl] }}
          />
          {lvl}
        </span>
      ))}
    </div>
  );
}
